import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaChalkboard, FaLayerGroup, FaPlus } from "react-icons/fa";
import Modal from "../../../components/Modal";
import SchoolYearDropdown, { getInitialSchoolYearId } from "../../../components/SchoolYearDropdown";
import { createGradeClassroom, createKindergartenClassrooms } from "../api";
import { addCachedClassrooms, loadSchoolYearList, loadTeacherList } from "../cache";
import { ActionButton, BackButton, Field, LdsPage, StatusBadge, cardEntryClass, inputClass } from "../components/Lds";
import type { Classroom, SchoolYear, Teacher } from "../types";
import { backendMessage, httpStatus, teacherName } from "../utils";
import { GRADE_BANDS, gradeBandGrades } from "./gradeBands";

import { useLeaveForm } from "../../../components/navigation/BackButton";
type SelectedClasses = Record<string, string>;

/** The school years in the shape of the shared school year dropdown. */
const toDropdownYears = (years: SchoolYear[]) =>
  years.map((year) => ({
    id: String(year.id),
    name: year.name ?? undefined,
    label: year.label ?? undefined,
    startDate: year.startDate instanceof Date ? year.startDate.toISOString() : year.startDate ?? undefined,
    endDate: year.endDate instanceof Date ? year.endDate.toISOString() : year.endDate ?? undefined,
  }));

const ClassroomNew = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const leaveForm = useLeaveForm();
  const [schoolYears, setSchoolYears] = useState<SchoolYear[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedClasses, setSelectedClasses] = useState<SelectedClasses>({});
  const [kindergartenCount, setKindergartenCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState<{ show: boolean; type: string; title: string; message: string; navigateOnClose?: string }>({ show: false, type: "", title: "", message: "" });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const [years, teacherList] = await Promise.all([loadSchoolYearList(), loadTeacherList()]);
        if (!cancelled) {
          setSchoolYears(years);
          setSelectedYear(getInitialSchoolYearId(toDropdownYears(years)));
          setTeachers(teacherList);
        }
      } catch {
        if (!cancelled) {
          setModal({
            show: true,
            type: "error",
            title: t("common.error", { defaultValue: "Error" }),
            message: t("classrooms.new.load_failed", { defaultValue: "Failed to load data." }),
          });
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const dropdownYears = useMemo(() => toDropdownYears(schoolYears), [schoolYears]);

  const predefinedClasses = useMemo<Record<string, string[]>>(
    () =>
      Object.fromEntries(
        GRADE_BANDS.map((band) => [
          t(`classrooms.common.levels.${band.key}`, { defaultValue: band.defaultLabel }),
          gradeBandGrades(band).map((grade) => `${t("classrooms.common.grade", { defaultValue: "Grade" })} ${grade}`),
        ]),
      ),
    [t]
  );

  const toggleClass = (className: string) => {
    setSelectedClasses((prev) => {
      const next = { ...prev };
      if (Object.prototype.hasOwnProperty.call(next, className)) delete next[className];
      else next[className] = "";
      return next;
    });
  };

  const toggleSection = (classList: string[]) => {
    setSelectedClasses((prev) => {
      const allSelected = classList.every((className) => Object.prototype.hasOwnProperty.call(prev, className));
      const next = { ...prev };
      classList.forEach((className) => {
        if (allSelected) delete next[className];
        else if (!Object.prototype.hasOwnProperty.call(next, className)) next[className] = "";
      });
      return next;
    });
  };

  const handleCreate = async () => {
    if (!selectedYear) {
      setModal({
        show: true,
        type: "error",
        title: t("common.validation_error", { defaultValue: "Validation error" }),
        message: t("classrooms.new.select_year_first", { defaultValue: "Please select a school year first." }),
      });
      return;
    }

    setSaving(true);
    try {
      const created: Classroom[] = [];
      if (kindergartenCount > 0) {
        created.push(...(await createKindergartenClassrooms(selectedYear, kindergartenCount)));
      }
      for (const [className, teacherId] of Object.entries(selectedClasses) as Array<[string, string]>) {
        const match = className.match(/\d+/);
        const grade = match ? Number(match[0]) : null;
        created.push(await createGradeClassroom(className, selectedYear, teacherId ? teacherId : null, grade));
      }
      addCachedClassrooms(created);
      setModal({
        show: true,
        type: "success",
        title: t("common.success", { defaultValue: "Success" }),
        message: t("classrooms.common.modal.success_message", { defaultValue: "Classrooms created successfully." }),
        navigateOnClose: "/school/classrooms",
      });
      setSelectedClasses({});
    } catch (err) {
      setModal({
        show: true,
        type: "error",
        title: t("common.error", { defaultValue: "Error" }),
        message:
          backendMessage(err) ||
          (httpStatus(err) === 403
            ? t("error.403.message", { defaultValue: "You do not have permission." })
            : t("classrooms.new.create_failed", { defaultValue: "Failed to create classrooms." })),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <LdsPage maxWidth="max-w-5xl">
      <div className="space-y-5">
        <BackButton fallback="/school/classrooms" />

        <section className="relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
          <div className="relative z-10 flex min-w-0 items-center gap-5 pr-0 lg:pr-[30%]">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-[24px] border-4 border-white bg-gradient-to-br from-indigo-200 to-sky-50 text-2xl text-indigo-700 shadow-lg dark:border-slate-900 dark:from-indigo-800 dark:to-sky-950 dark:text-indigo-100 sm:h-24 sm:w-24">
              <FaPlus aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-indigo-600 dark:text-indigo-300">
                {t("classrooms.overview.title", { defaultValue: "Classrooms" })}
              </p>
              <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                {t("classrooms.new.title", { defaultValue: "New classrooms" })}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                {t("classrooms.new.subtitle", { defaultValue: "Plan multiple classrooms at once." })}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <StatusBadge tone={selectedYear ? "info" : "warn"}>
                  {selectedYear ? t("classrooms.new.selected_year", { defaultValue: "School year selected" }) : t("classrooms.new.select_year_status", { defaultValue: "Select a school year" })}
                </StatusBadge>
                <StatusBadge tone="neutral"><span className="inline-flex items-center gap-1.5"><FaLayerGroup aria-hidden="true" />{t("classrooms.new.batch_badge", { defaultValue: "Batch creation" })}</span></StatusBadge>
              </div>
            </div>
          </div>
          <div className="pointer-events-none absolute inset-y-0 right-5 hidden items-center lg:flex" aria-hidden="true">
            <FaChalkboard className="text-[120px] text-indigo-200/70 dark:text-indigo-800/40" />
          </div>
        </section>

        <section className={`${cardEntryClass} rounded-[26px] border border-slate-200/80 bg-white/95 p-5 shadow-sm dark:border-white/10 dark:bg-white/5 sm:p-6`}>
          <div className="grid gap-5 sm:grid-cols-2">
            <SchoolYearDropdown
              schoolYears={dropdownYears}
              selectedSchoolYearId={selectedYear}
              onChange={setSelectedYear}
              label={t("classrooms.new.select_year", { defaultValue: "Select school year" })}
            />
            <Field label={t("classrooms.new.kindergarten_amount", { defaultValue: "Kindergarten amount" })}>
              <input
                type="number"
                min="0"
                value={kindergartenCount}
                onChange={(event) => setKindergartenCount(Math.max(0, Number(event.target.value)))}
                className={inputClass}
              />
            </Field>
          </div>

          <div className="mt-6 space-y-5">
            {(Object.entries(predefinedClasses) as Array<[string, string[]]>).map(([section, classList]) => {
              const allSelected = classList.every((className) => Object.prototype.hasOwnProperty.call(selectedClasses, className));
              return (
                <div key={section} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 dark:border-white/10 dark:bg-slate-950/40">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h2 className="text-sm font-semibold uppercase text-slate-600 dark:text-slate-300">{section}</h2>
                    <button type="button" className="text-sm font-semibold text-indigo-700 dark:text-indigo-200" onClick={() => toggleSection(classList)}>
                      {allSelected ? t("common.deselect_all", { defaultValue: "Deselect all" }) : t("common.select_all", { defaultValue: "Select all" })}
                    </button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {classList.map((className) => {
                      const selected = Object.prototype.hasOwnProperty.call(selectedClasses, className);
                      return (
                        <div key={className} className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-white/10 dark:bg-white/5">
                          <label className="flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-100">
                            <input type="checkbox" checked={selected} onChange={() => toggleClass(className)} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                            {className}
                          </label>
                          {selected ? (
                            <select
                              className={`${inputClass} mt-3 text-sm`}
                              value={selectedClasses[className]}
                              onChange={(event) => setSelectedClasses((prev) => ({ ...prev, [className]: event.target.value }))}
                            >
                              <option value="">{t("classrooms.new.no_teacher_assigned", { defaultValue: "No teacher assigned" })}</option>
                              {teachers.map((teacher) => (
                                <option key={String(teacher.id)} value={String(teacher.id)}>
                                  {teacherName(teacher)}
                                </option>
                              ))}
                            </select>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="sticky bottom-4 z-20 mt-8 flex flex-wrap items-center justify-end gap-3 rounded-2xl border border-slate-200/80 bg-white/95 p-3 shadow-lg backdrop-blur dark:border-white/10 dark:bg-slate-950/90">
            <ActionButton tone="secondary" onClick={() => navigate("/school/classrooms")}>{t("common.cancel", { defaultValue: "Cancel" })}</ActionButton>
            <ActionButton disabled={saving} onClick={handleCreate}>{saving ? t("common.saving", { defaultValue: "Saving..." }) : t("common.create", { defaultValue: "Create" })}</ActionButton>
          </div>
        </section>
      </div>

      <Modal
        show={modal.show}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onClose={() => {
          setModal({ show: false, type: "", title: "", message: "" });
          if (modal.navigateOnClose) leaveForm(modal.navigateOnClose);
        }}
      />
    </LdsPage>
  );
};

export default ClassroomNew;
